import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { addDays } from 'date-fns';
import { Casa, Tarea } from '../types';
import { esApp } from './plataforma';
import { claveDia, mediodia, meAvisa, ocurrencias } from './tareas';

/**
 * Avisos de las tareas que te tocan (solo en la app). Son notificaciones
 * locales, como los recordatorios de gastos: las programa el propio iPhone,
 * sin servidor. Se reprograman al abrir la app y cada vez que cambian las
 * tareas, así que lo que ya está hecho no avisa.
 *
 * Avisan las tuyas, las de «Mis tareas» y las de «Cualquiera» de tus casas.
 * - Una tarea con hora avisa a esa hora.
 * - Las que no tienen hora van juntas en un aviso por la mañana: «Hoy te toca…».
 *
 * Los ajustes son de este dispositivo (Preferences), como el permiso de iOS.
 */

export interface AjustesAvisosTareas {
  activos: boolean;
  /** "HH:MM" del aviso de la mañana. */
  horaResumen: string;
}

const POR_DEFECTO: AjustesAvisosTareas = { activos: false, horaResumen: '09:00' };
const CLAVE = 'avisosTareas';

/** Días que se dejan programados. */
const DIAS = 7;
/** iOS admite 64 avisos pendientes por app; los recordatorios de gastos usan unos 17. */
const MAX_AVISOS = 40;
const ID_BASE = 7300;
const IDS = Array.from({ length: MAX_AVISOS }, (_, i) => ID_BASE + i);
const RUTA = '/tareas';

export async function leerAjustesTareas(): Promise<AjustesAvisosTareas> {
  const { value } = await Preferences.get({ key: CLAVE });
  if (!value) return POR_DEFECTO;
  try { return { ...POR_DEFECTO, ...JSON.parse(value) }; } catch { return POR_DEFECTO; }
}

/** Guarda y, si se encienden, pide permiso: devuelve false si no lo da. */
export async function guardarAjustesTareas(ajustes: AjustesAvisosTareas): Promise<boolean> {
  if (ajustes.activos) {
    const { display } = await LocalNotifications.checkPermissions();
    if (display !== 'granted') {
      const pedido = await LocalNotifications.requestPermissions();
      if (pedido.display !== 'granted') return false;
    }
  }
  await Preferences.set({ key: CLAVE, value: JSON.stringify(ajustes) });
  return true;
}

export async function cancelarAvisosTareas(): Promise<void> {
  if (!esApp) return;
  await LocalNotifications.cancel({ notifications: IDS.map(id => ({ id })) });
}

const enLaHora = (dia: Date, hora: string) => {
  const [h, m] = hora.split(':').map(Number);
  const d = new Date(dia);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
};

const lista = (nombres: string[]) =>
  nombres.length === 1 ? nombres[0] : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;

/** Lo que hay que programar: puro, para poder probarlo. */
export function avisosAProgramar(
  tareas: Tarea[], casas: Casa[], uid: string | undefined, ajustes: AjustesAvisosTareas, ahora: Date = new Date(),
) {
  if (!ajustes.activos) return [];
  const porCasa = new Map(casas.map(c => [c.id, c]));
  const mias = tareas.filter(t => meAvisa(t, porCasa.get(t.casaId), uid));
  const hoy = mediodia(ahora);
  const proximas = ocurrencias(mias, [], hoy, addDays(hoy, DIAS - 1), hoy)
    .filter(o => o.estado === 'pendiente' || o.estado === 'futura');

  const avisos: { id: number; title: string; body: string; schedule: { at: Date; allowWhileIdle: boolean }; extra: { ruta: string } }[] = [];
  // Con hora: a su hora
  for (const o of proximas.filter(o => o.tarea.hora)) {
    const at = enLaHora(o.fecha, o.tarea.hora!);
    if (at <= ahora) continue;
    avisos.push({
      id: 0, title: `${o.tarea.icono} ${o.tarea.nombre}`,
      body: o.tarea.asignadoA || porCasa.get(o.tarea.casaId)?.personal
        ? 'Te toca ahora. Márcala cuando la hagas.'
        : 'Toca ahora y la puede hacer cualquiera. Márcala cuando la hagas.',
      schedule: { at, allowWhileIdle: true }, extra: { ruta: RUTA },
    });
  }
  // Sin hora: juntas, por la mañana
  const sinHora = new Map<string, string[]>();
  for (const o of proximas.filter(o => !o.tarea.hora)) {
    const k = claveDia(o.fecha);
    sinHora.set(k, [...(sinHora.get(k) ?? []), o.tarea.nombre]);
  }
  for (const [k, nombres] of Array.from(sinHora)) {
    const at = enLaHora(new Date(`${k}T12:00:00`), ajustes.horaResumen);
    if (at <= ahora) continue;
    avisos.push({
      id: 0, title: nombres.length === 1 ? 'Hoy te toca una tarea' : `Hoy te tocan ${nombres.length} tareas`,
      body: lista(nombres.map(n => n.charAt(0).toLowerCase() + n.slice(1))).replace(/^./, c => c.toUpperCase()) + '.',
      schedule: { at, allowWhileIdle: true }, extra: { ruta: RUTA },
    });
  }
  return avisos
    .sort((a, b) => a.schedule.at.getTime() - b.schedule.at.getTime())
    .slice(0, MAX_AVISOS)
    .map((a, i) => ({ ...a, id: ID_BASE + i }));
}

export async function reprogramarAvisosTareas(tareas: Tarea[], casas: Casa[], uid: string | undefined): Promise<void> {
  if (!esApp) return;
  await cancelarAvisosTareas();
  const ajustes = await leerAjustesTareas();
  if (!ajustes.activos) return;
  const { display } = await LocalNotifications.checkPermissions();
  if (display !== 'granted') return;
  const notifications = avisosAProgramar(tareas, casas, uid, ajustes);
  if (notifications.length) await LocalNotifications.schedule({ notifications });
}
